import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useLocation, useNavigate } from 'react-router-dom'

import { useDispatch } from 'react-redux'

import styles from './Login.module.css'

import EntryForm, { EntryFormContainer, EntryFormError } from 'Layout/EntryForm'
import Heading from 'Design/components/Heading'
import FormControl from 'Design/components/FormControl'
import Label from 'Design/components/Label'
import Input from 'Design/components/Input'
import FormErrorText from 'Design/components/FormErrorText'
import Button from 'Design/components/Button'
import Checkbox from 'Design/components/Checkbox'

import loginFormValidationSchema from './login-form-validation-schema'
import { useLoginMutation } from './login.service'

import { loadRecaptcha } from '../../../utils/recaptcha'

import { setCsrfToken } from 'Actions/app'

const { RECAPTCHA_SITE_KEY } = process.env.CONFIG

const TitleMessage = () => {
  const { state } = useLocation()

  if (state === 'accountVerificationSuccess') {
    return 'Account verification successful 👍'
  }

  if (state === 'setPasswordSuccess') {
    return 'Password updated successfully 👍'
  }

  return 'Hey! 👋'
}

const Login = () => {
  const {
    handleSubmit,
    register,
    control,
    formState: { errors, isSubmitting },
    setError,
    clearErrors
  } = useForm({
    defaultValues: {
      email: '',
      password: '',
      remember: true,
      apiError: '' // not a form field
    },
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: yupResolver(loginFormValidationSchema)
  })

  const [login, loginResult] = useLoginMutation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const [captchaRendered, setCaptchaRendered] = useState(false)
  const [requireRecaptcha, setRequireRecaptcha] = useState(false)

  const processError = (err = {}) => {
    switch (err.type) {
      case 'INVALID_EMAIL':
        setError('email', err, { shouldFocus: true })
        break

      case 'INVALID_PASSWORD':
      case 'PASSWORD_REQUIRED':
        setError('password', err, { shouldFocus: true })
        break

      default:
        setError('apiError', err)
    }
  }

  const onFormSubmit = async (data) => {
    clearErrors('apiError')

    if (requireRecaptcha) {
      if (window.grecaptcha) {
        window.grecaptcha.execute()
        return
      }

      processError({
        type: 'RECAPTCHA_ERROR',
        message: 'Unable to load Google reCAPTCHA. Try reloading the page.'
      })
      return
    }

    submitForm(data, '')
  }

  const submitForm = async (data, recaptchaToken) => {
    const payload = {
      email: data.email,
      password: data.password,
      remember: data.remember
    }

    if (recaptchaToken) {
      payload.recaptcha_token = recaptchaToken
    }

    try {
      const { csrfToken, ...user } = await login(payload).unwrap()

      localStorage.setItem('auth', true)

      dispatch(setCsrfToken({ token: csrfToken }))

      let target = '/dashboard'
      if (location.state && location.state.from) {
        target = location.state.from
      } else {
        target = { pathname: user.new_ui ? '/b/dashboard' : '/dashboard' }
      }

      navigate(target)
    } catch (e) {
      console.error('Unable to login', e)

      const error = e.data || {}

      if (error?.data?.requireRecaptcha) {
        if (recaptchaToken !== '' || captchaRendered) {
          window.grecaptcha.reset()
        } else {
          await loadRecaptcha()
          window.grecaptcha.render('login-recaptcha', {
            callback: handleSubmit(submitForm),
            'expired-callback': () => {
              processError({
                type: 'RECAPTCHA_ERROR',
                message: 'ReCaptcha expired. Please try again.'
              })
            },
            'error-callback': () => {
              processError({
                type: 'RECAPTCHA_ERROR',
                message: 'ReCaptcha verification failed. Please reload the page.'
              })
            }
          })
          setCaptchaRendered(true)
        }

        setRequireRecaptcha(true)
      } else {
        setRequireRecaptcha(false)
      }

      switch (error.type) {
        case 'VALIDATION_ERR':
          processError(error.errors[0])
          break

        default:
          processError(error)
      }
    }
  }

  const handleForgotPassword = (e) => {
    navigate('/reset-password')
  }

  const isLoggingIn = loginResult.isLoading || isSubmitting

  return (
    <EntryForm>
      <EntryFormContainer>
        {errors.apiError && <EntryFormError>{errors.apiError.message}</EntryFormError>}

        <Heading level={2} className={styles.LoginTitle}>
          <TitleMessage />
        </Heading>

        <form className={styles.LoginForm} onSubmit={handleSubmit(onFormSubmit)}>
          <FormControl isInvalid={errors.email}>
            <Label htmlFor='user-email'>Email</Label>
            <Input type='text' id='user-email' {...register('email')} placeholder='Enter your work email address' />
            {errors.email && <FormErrorText>{errors.email.message}</FormErrorText>}
          </FormControl>

          <FormControl isInvalid={errors.password}>
            <Label htmlFor='password'>Password</Label>
            <Input type='password' id='password' {...register('password')} placeholder='Enter your password' />
            {errors.password && <FormErrorText>{errors.password.message}</FormErrorText>}
          </FormControl>

          <FormControl>
            <Controller
              control={control}
              name='remember'
              render={({ field: { value, ...rest } }) => {
                return (
                  <Checkbox id='rememberMe' checked={value} {...rest}>
                    Remember me
                  </Checkbox>
                )
              }}
            />
          </FormControl>

          <div className='form-group'>
            <div id='login-recaptcha' className='g-recaptcha' data-sitekey={RECAPTCHA_SITE_KEY} data-size='invisible' />
          </div>

          <Button variant='primary' type='submit' disabled={isLoggingIn}>
            {isLoggingIn ? 'Logging in' : 'Login'}
          </Button>

          <div className={styles.ForgotPassword} role='link' onClick={handleForgotPassword}>
            Forgot Password?
          </div>
        </form>
      </EntryFormContainer>
    </EntryForm>
  )
}

export default Login
